import { datadogRum } from "@datadog/browser-rum";
import { isDefiniteChunkLoadError } from "src/lib/monitoring";
import { sleep } from "src/utils/promise";

const CHUNK_RELOAD_KEY = "chunk-reload";

/**
 * Build SHAs we've already taken a deploy-skew reload for. Separate from the
 * poison/network budget under CHUNK_RELOAD_KEY: a skew reload is a single
 * URL-preserving hop to the fresh build, guarded to once per build so a
 * not-yet-propagated CDN edge can't loop it.
 */
const DEPLOY_SKEW_RELOAD_KEY = "chunk-skew-reload";

/** Max auto-reloads for a single build before we give up and crash. */
export const MAX_CHUNK_RELOADS_PER_BUILD = 3;

/**
 * How many recent builds we remember reload counts for. Bounds the rare
 * two-build flap during a rollout (the revisited SHAs stay tracked and fill
 * their budgets) without ever capping forward progress across new deploys.
 */
const MAX_TRACKED_BUILDS = 8;

type BuildReloads = { sha: string; count: number };
type ChunkReloadState = { builds: BuildReloads[] };

/**
 * The build this document is actually running, baked into index.html at build
 * time (see vite.config.ts) and read by datadog RUM too. A reload that lands on
 * a newer build changes this value — our proof that the reload made progress.
 */
export function currentBuildSha(): string {
  return document.getElementById("react")?.dataset.commitSha ?? "unknown";
}

/** Safe stringification matching monitoring.tsx conventions. */
export function stringifyError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

// Asset URLs as they appear in chunk-load error messages. The absolute form
// (Chrome/Firefox import errors) requires the host to be followed directly by
// /assets/. The root-relative form (Vite's "Unable to preload CSS for
// /assets/..." message) must start the string or follow whitespace/quote/
// paren, so a cross-origin URL with a path prefix (https://cdn.x/eu/assets/y.js)
// can't leak a fabricated same-origin /assets/ match via backtracking.
// Safari messages carry no URL.
const ABSOLUTE_ASSET_URL_PATTERN =
  /https?:\/\/[^/\s]+\/assets\/[^\s'")?]+\.(?:js|css)/;
const RELATIVE_ASSET_URL_PATTERN =
  /(?:^|[\s'"(`])(\/assets\/[^\s'")?]+\.(?:js|css))/;

/** Same-origin /assets/ URL from a chunk-load error message, or null. */
export function extractAssetUrl(error: unknown): URL | null {
  const message = stringifyError(error);
  const candidate =
    message.match(ABSOLUTE_ASSET_URL_PATTERN)?.[0] ??
    message.match(RELATIVE_ASSET_URL_PATTERN)?.[1];
  if (!candidate) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(candidate, window.location.origin);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) {
    return null;
  }
  if (!url.pathname.startsWith("/assets/")) {
    return null;
  }
  return url;
}

/** Cap on how long the pre-reload cache-overwrite fetch may delay the reload. */
export const CACHE_OVERWRITE_TIMEOUT_MS = 3_000;

/**
 * Replace the HTTP cache entry for an asset with a cache-bypassing fetch.
 * Drains (not cancels) the body so the replacement entry is complete; the
 * bytes aren't wasted — a retry or the post-reload document imports this same
 * asset.
 */
export async function overwriteCachedAsset(url: URL): Promise<Response> {
  const response = await fetch(url.href, { cache: "reload" });
  await response.arrayBuffer().catch(() => {});
  return response;
}

/**
 * A poisoned HTTP cache entry — HTML or an error response stored under the
 * chunk's URL by the CDN misconfiguration this works around (see
 * vercel.json) — survives soft reloads, so reloading alone can never recover.
 * Overwrite the entry with a cache-bypassing fetch first, then reload.
 * Best-effort: the reload proceeds after a timeout or fetch failure, and also
 * heals clients whose cache was poisoned before the CDN config fix shipped.
 */
async function overwriteThenReload(assetUrl: URL): Promise<void> {
  // The reload is the one step that must always happen — once the caller has
  // latched reloadScheduled, an aborted pre-reload step would otherwise leave
  // the page stuck forever on the boundaries' pending-reload spinner.
  try {
    const outcome = await Promise.race([
      overwriteCachedAsset(assetUrl).then(
        () => "overwritten" as const,
        () => "failed" as const,
      ),
      sleep(CACHE_OVERWRITE_TIMEOUT_MS).then(() => "timeout" as const),
    ]);
    // Flushed via sendBeacon on pagehide, so it survives the reload below.
    datadogRum.addAction("chunk:cache-overwrite", {
      build_sha: currentBuildSha(),
      asset_url: assetUrl.href,
      outcome,
    });
  } finally {
    window.location.reload();
  }
}

// Dedupe the budget-exhausted signal: the router error boundary calls
// reloadForChunkError() from its render body, so post-exhaustion re-renders
// would otherwise emit one "user saw the crash UI" action per render.
let exhaustedReportedSha: string | null = null;

// Once a reload is committed it may still be deferred by the cache-overwrite
// fetch (up to CACHE_OVERWRITE_TIMEOUT_MS); chunk errors surfacing during
// that window (e.g. error-boundary re-renders) must not burn more budget —
// the pending reload supersedes them.
let reloadScheduled = false;

/** Test-only reset for module-level state (each page load starts fresh). */
export function __resetChunkReloadStateForTests() {
  exhaustedReportedSha = null;
  reloadScheduled = false;
}

function readState(): ChunkReloadState {
  try {
    const raw = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!raw) {
      return { builds: [] };
    }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed?.builds)) {
      const builds = parsed.builds.filter(
        (b: unknown): b is BuildReloads =>
          typeof (b as BuildReloads)?.sha === "string" &&
          typeof (b as BuildReloads)?.count === "number",
      );
      return { builds };
    }
    return { builds: [] };
  } catch {
    return { builds: [] };
  }
}

/** Returns false if the write fails, so the caller skips reloading. */
function writeState(state: ChunkReloadState): boolean {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
}

function readSkewReloadedShas(): string[] {
  try {
    const raw = sessionStorage.getItem(DEPLOY_SKEW_RELOAD_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((s): s is string => typeof s === "string")
      : [];
  } catch {
    return [];
  }
}

/** Returns false if the write fails, so the caller skips reloading. */
function writeSkewReloadedShas(shas: string[]): boolean {
  try {
    sessionStorage.setItem(DEPLOY_SKEW_RELOAD_KEY, JSON.stringify(shas));
    return true;
  } catch {
    return false;
  }
}

/**
 * Auto-reload when a stale chunk fails to load after a deploy.
 *
 * The budget is keyed to the running build's commit SHA, NOT a timer. A reload
 * that lands on a newer build changes the SHA — proof the reload made progress
 * — so that build starts with a fresh budget. A reload that keeps landing on
 * the same (still-broken or not-yet-propagated) build does not change the SHA,
 * so its count climbs and we stop after MAX_CHUNK_RELOADS_PER_BUILD, falling
 * through to the crash UI instead of looping.
 *
 * Counts are kept per build in a small LRU, so there is no lifetime cap:
 * distinct new builds always get a fresh budget (a long-lived tab keeps
 * recovering across deploys), while a mid-rollout flap between a few builds
 * accumulates on those revisited SHAs and terminates.
 *
 * This needs no "healthy render" signal, so it can't wedge: the cap only ever
 * suppresses *auto*-reloads of a build that keeps failing. The document always
 * revalidates and nothing pins it (skew protection is off), so a manual reload
 * or any new deploy moves to the latest build — a new SHA and a fresh budget —
 * and a user can never get permanently stuck on a build.
 */
export function reloadForChunkError(error?: unknown): boolean {
  if (reloadScheduled) {
    return true;
  }

  const sha = currentBuildSha();
  const { builds } = readState();
  const assetUrl = extractAssetUrl(error);

  const existing = builds.find((b) => b.sha === sha);
  if (existing && existing.count >= MAX_CHUNK_RELOADS_PER_BUILD) {
    if (exhaustedReportedSha !== sha) {
      exhaustedReportedSha = sha;
      // The user is about to see the crash UI — the signal worth alerting on.
      datadogRum.addAction("chunk:reload", {
        build_sha: sha,
        reload_count: existing.count,
        budget_exhausted: true,
        message: stringifyError(error),
        asset_url: assetUrl?.href ?? null,
      });
    }
    return false;
  }

  // Most-recently-touched build goes last; drop the oldest beyond the cap.
  const others = builds.filter((b) => b.sha !== sha);
  const nextCount = (existing?.count ?? 0) + 1;
  const nextBuilds = [...others, { sha, count: nextCount }].slice(
    -MAX_TRACKED_BUILDS,
  );

  if (!writeState({ builds: nextBuilds })) {
    return false;
  }

  reloadScheduled = true;

  // A failure in any pre-reload step must never leave the latch set with no
  // reload coming (a permanently blank page); degrade to a plain reload.
  try {
    // The RUM SDK flushes via sendBeacon on pagehide, so this survives the
    // reload in practice; the budget-exhausted emission above has no reload
    // race at all.
    datadogRum.addAction("chunk:reload", {
      build_sha: sha,
      reload_count: nextCount,
      budget_exhausted: false,
      message: stringifyError(error),
      asset_url: assetUrl?.href ?? null,
    });

    if (assetUrl) {
      void overwriteThenReload(assetUrl);
      return true;
    }
  } catch {
    // fall through to the plain reload
  }
  window.location.reload();
  return true;
}

/**
 * One reload for a deploy-skew chunk failure — a 404 or SPA-HTML served at an
 * asset path, marked by the retry ladder (see chunk-retry.ts). The running
 * build's index.html points at chunks the current deploy deleted; a single
 * reload fetches the fresh (uncached) index.html and its live manifest.
 * window.location.reload() preserves the current URL — the Vercel SPA rewrite
 * restores the route from it.
 *
 * Guarded to one reload per build SHA via a sessionStorage set, independent of
 * the poison/network 3-try budget: a skew reload normally lands on a NEW build
 * (new SHA) that works. If a not-yet-propagated edge keeps serving the same
 * stale build the SHA repeats, this returns false on the second skew, and the
 * boundary falls through to the crash UI instead of looping. No pre-reload
 * refetch — the asset is gone, so only a fresh index.html can help.
 */
export function reloadForDeploySkew(error?: unknown): boolean {
  if (reloadScheduled) {
    return true;
  }

  const sha = currentBuildSha();
  const reloaded = readSkewReloadedShas();
  if (reloaded.includes(sha)) {
    return false;
  }

  const next = [...reloaded, sha].slice(-MAX_TRACKED_BUILDS);
  if (!writeSkewReloadedShas(next)) {
    return false;
  }

  reloadScheduled = true;
  datadogRum.addAction("chunk:reload", {
    build_sha: sha,
    reason: "deploy-skew",
    budget_exhausted: false,
    message: stringifyError(error),
    asset_url: extractAssetUrl(error)?.href ?? null,
  });
  window.location.reload();
  return true;
}

/**
 * User-initiated recovery from the crash UI's "Reload page" button. A bare
 * location.reload() re-serves a poisoned HTTP cache entry (the content-hashed
 * chunk is immutable), so the button could never recover a poisoned tab — the
 * only cure was a cache clear / incognito. This overwrites the failed asset's
 * cache entry with a cache-bypassing fetch first, then reloads.
 *
 * Consults no budget: the user explicitly asked to recover, so it always
 * overwrites-then-reloads (bounded by CACHE_OVERWRITE_TIMEOUT_MS) even after
 * the auto-reload budget is exhausted. Falls back to a plain reload when no
 * asset URL was captured from the crash.
 */
export async function recoverAndReload(assetUrl: URL | null): Promise<void> {
  reloadScheduled = true;
  if (!assetUrl) {
    window.location.reload();
    return;
  }
  await overwriteThenReload(assetUrl);
}

/**
 * Suppress chunk-load rejections that escape as unhandled promise rejections.
 *
 * No vite:preloadError handler — calling preventDefault() on that event
 * causes Vite to resolve the dynamic import as undefined (instead of
 * rejecting), which crashes React with an unrecoverable TypeError.
 * Not calling preventDefault() lets errors propagate naturally:
 * - Navigation errors reach React error boundaries -> auto-reload
 * - Prefetch errors are caught by Link's .catch(() -> {})
 *
 * The unhandledrejection listener is a safety net for chunk-load
 * rejections that slip through (e.g. prefetches outside our custom Link).
 * It only suppresses console noise — reload decisions live exclusively
 * in ErrorBoundary.componentDidCatch.
 */
export function installChunkLoadErrorHandler() {
  window.addEventListener("unhandledrejection", (event) => {
    if (isDefiniteChunkLoadError(event.reason)) {
      event.preventDefault();
    }
  });
}
